'use client';

import React, { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { sortAlbumLocalesForUser } from '@/shared/constants';
import { useUser } from '@/app/model/stores';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/shared/ui/shadcn/select';
import { cn } from '@/shared/lib/cn';

interface AlbumLanguageSelectorProps {
  value: string;
  onChange: (language: string) => void;
  disabled?: boolean;
  className?: string;
}

export function AlbumLanguageSelector({
  value,
  onChange,
  disabled = false,
  className = '',
}: AlbumLanguageSelectorProps) {
  const user = useUser();

  // Sort languages with user's locale first, then English, then others
  const sortedLanguages = useMemo(() => {
    return sortAlbumLocalesForUser(user?.locale);
  }, [user?.locale]);

  // Get display name for selected language
  const selectedLanguage = sortedLanguages.find((lang) => lang.code === value);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        id="album-language"
        className={cn(
          'border-0 bg-transparent h-auto p-0 w-auto gap-1 text-primary hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden',
          className
        )}
      >
        <span className="text-form-label">
          {selectedLanguage?.name || value}
        </span>
        <ChevronDown className="h-4 w-4 text-primary" />
      </SelectTrigger>
      <SelectContent>
        {sortedLanguages.map((language) => (
          <SelectItem key={language.code} value={language.code}>
            {language.name} ({language.nativeName})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
